import React, { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "./ui/dialog";
import { Button } from "./ui/button";
import { CloudDownload, Cloud, Check } from "lucide-react";
import { getOfflineSettings, saveOfflineSettings, syncNow, clearOfflineData, subscribeOffline, state as syncState } from "../lib/offlineSync";

const Opt = ({ active, onClick, label, hint, testid }) => (
  <button type="button" data-testid={testid} onClick={onClick}
    className={`w-full text-left rounded-xl border px-3 py-2.5 transition-colors ${active ? "border-primary bg-primary/10" : "hover:bg-muted"}`}>
    <div className="flex items-center gap-2 text-sm font-medium">{active && <Check className="h-4 w-4 text-primary" />}{label}</div>
    {hint && <div className="text-xs text-muted-foreground mt-0.5">{hint}</div>}
  </button>
);

/** "Quer o modo offline neste telefone?" — first login on a phone, or from Settings. */
export const OfflineSetupDialog = ({ open, onClose, user, t, firstRun = false }) => {
  const cur = getOfflineSettings(user.id) || {};
  const [history, setHistory] = useState(cur.history || "90d");
  const [photos, setPhotos] = useState(cur.photos || "thumb");
  const [step, setStep] = useState("choose"); // choose | downloading
  const [prog, setProg] = useState(0);
  useEffect(() => subscribeOffline((type, d) => { if (type === "state") setProg(d.progress); }), []);
  useEffect(() => { if (open) { const c = getOfflineSettings(user.id) || {}; setHistory(c.history || "90d"); setPhotos(c.photos || "thumb"); setStep("choose"); } }, [open, user.id]);

  const enable = async () => {
    saveOfflineSettings(user.id, { enabled: true, history, photos, since: new Date().toISOString() });
    setStep("downloading");
    await syncNow(user, { full: true });
    onClose();
  };
  const onlineOnly = async () => {
    saveOfflineSettings(user.id, { enabled: false });
    await clearOfflineData(user.id);
    onClose();
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v && step !== "downloading") onClose(); }}>
      <DialogContent className="max-w-md" data-testid="offline-setup-dialog">
        <DialogHeader>
          <DialogTitle className="font-display flex items-center gap-2"><CloudDownload className="h-5 w-5 text-primary" />{t("offline_setup_title")}</DialogTitle>
        </DialogHeader>
        {step === "choose" ? (
          <div className="space-y-4">
            <p className="text-sm text-muted-foreground">{firstRun ? t("offline_setup_intro") : t("offline_setup_intro_settings")}</p>
            <div>
              <div className="text-xs font-semibold uppercase tracking-wide text-muted-foreground mb-1.5">{t("offline_history")}</div>
              <div className="grid gap-1.5">
                <Opt testid="offline-history-90d" active={history === "90d"} onClick={() => setHistory("90d")} label={t("offline_history_90d")} />
                <Opt testid="offline-history-12m" active={history === "12m"} onClick={() => setHistory("12m")} label={t("offline_history_12m")} />
                <Opt testid="offline-history-all" active={history === "all"} onClick={() => setHistory("all")} label={t("offline_history_all")} hint={t("offline_history_all_hint")} />
              </div>
            </div>
            <div>
              <div className="text-xs font-semibold uppercase tracking-wide text-muted-foreground mb-1.5">{t("offline_photos")}</div>
              <div className="grid gap-1.5">
                <Opt testid="offline-photos-thumb" active={photos === "thumb"} onClick={() => setPhotos("thumb")} label={t("offline_photos_thumb")} hint={t("offline_photos_thumb_hint")} />
                <Opt testid="offline-photos-full" active={photos === "full"} onClick={() => setPhotos("full")} label={t("offline_photos_full")} hint={t("offline_photos_full_hint")} />
              </div>
              <div className="text-xs text-muted-foreground mt-2">{t("offline_voice_note")}</div>
            </div>
            <div className="flex flex-col gap-2 pt-1">
              <Button data-testid="offline-enable-btn" onClick={enable}><CloudDownload className="h-4 w-4 mr-2" />{t("offline_enable_btn")}</Button>
              <Button variant="outline" data-testid="offline-online-only-btn" onClick={onlineOnly}><Cloud className="h-4 w-4 mr-2" />{t("offline_online_only_btn")}</Button>
            </div>
          </div>
        ) : (
          <div className="space-y-3 py-2" data-testid="offline-downloading">
            <p className="text-sm">{t("offline_downloading")}</p>
            <div className="h-2 rounded-full bg-muted overflow-hidden"><div className="h-full bg-primary transition-all" style={{ width: `${prog}%` }} /></div>
            <div className="text-xs text-muted-foreground">{prog}% · {t("offline_downloading_hint")}</div>
            {syncState.full && <p className="text-xs text-destructive">{t("offline_full_short")}</p>}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};
